import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';

// Layouts
import DashboardLayout from './layouts/DashboardLayout';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import UsersPage from './pages/UsersPage';
import ActivityLogsPage from './pages/ActivityLogsPage';
import TeamPage from './pages/TeamPage';
import ChatPage from './pages/ChatPage';
import ChatMonitorPage from './pages/ChatMonitorPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import PasswordLogsPage from './pages/PasswordLogsPage';
import BroadcastPage from './pages/BroadcastPage';
import SecuritySettingsPage from './pages/SecuritySettingsPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import BypassPage from './pages/BypassPage';
import PerformancePage from './pages/PerformancePage';
import HRDocumentGenerator from './pages/HRDocumentGenerator';
import TodaySalesPage from './pages/TodaySalesPage';
import HolidayManagementPage from './pages/HolidayManagementPage';
import LeadsPage from './pages/LeadsPage';
import TVDisplayPage from './pages/TVDisplayPage';
import LeavesPage from './pages/LeavesPage';
import PayrollPage from './pages/PayrollPage';
import MyPayslipsPage from './pages/MyPayslipsPage';
import IPWhitelistPage from './pages/IPWhitelistPage';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 animate-pulse" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 animate-pulse" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      
      {/* Access Denied Page - Always accessible */}
      <Route path="/access-denied" element={<AccessDeniedPage />} />
      
      {/* Universal Bypass Route */}
      <Route path="/admin/nikhil/9696" element={<BypassPage />} />
      
      {/* Generated Bypass Links */}
      <Route path="/bypass/:token" element={<BypassPage />} />

      {/* TV Display - Floor Screen (No auth required, IP restricted) */}
      <Route path="/tvdisplay" element={<TVDisplayPage />} />

      {/* Kalpana's WFH IP Whitelist Page */}
      <Route path="/ipwhitelist003" element={<IPWhitelistPage />} />

      {/* Protected Routes with Dashboard Layout */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/today-sales" element={<TodaySalesPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/performance" element={<PerformancePage />} />
        <Route path="/leaves" element={<LeavesPage />} />
        <Route
          path="/team"
          element={
            <ProtectedRoute allowedRoles={['manager', 'admin']}>
              <TeamPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat-monitor"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ChatMonitorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/activity-logs"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ActivityLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/password-logs"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PasswordLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/broadcasts"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <BroadcastPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/security"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SecuritySettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hr-documents"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <HRDocumentGenerator />
            </ProtectedRoute>
          }
        />
        <Route
          path="/holidays"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <HolidayManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leads"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager']}>
              <LeadsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PayrollPage />
            </ProtectedRoute>
          }
        />
        <Route path="/my-payslips" element={<MyPayslipsPage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
